import { useAppSelector } from '../../../../store/redux/hooks';
import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  Circle,
  Text,
  useColorModeValue,
  HStack,
  Image,
  IconButton,
  Center,
  useToast,
  Link,
} from '@chakra-ui/react';
import ReactMarkdown from 'react-markdown';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { ICON_BASE64 } from '../../../../utils/base64';
import CopyIconImage from '../../../../theme/icons/copy.svg';
import CustomToast from '../../../../common/CustomToast';
import { brandingFromTemplate, deriveTheme } from './formTheme';
import {
  hasDefaultBranding,
  templateIcon,
  templateLogo,
} from './templateBranding';

const HIGHLIGHT_BORDER = '2px solid';
const HIGHLIGHT_COLOR = '#FF4935';

const FORM_CONSENT_LONG_HEIGHT = 152;
const FORM_CONSENT_TEXT_WIDTH = {
  Desktop: { withCheckbox: 334, textOnly: 366 },
  Mobile: { withCheckbox: 252, textOnly: 284 },
};
const PREVIEW_CONSENT_COLLAPSED = 100;

const CustomFormPreviewPanel = () => {
  const [active, setActive] = useState('Desktop');
  const buttons = ['Desktop', 'Mobile'];
  const headerBg = useColorModeValue('gray.200', 'gray.700');
  const simulatorBg = useColorModeValue('gray.100', 'gray.800');
  const addressBarBg = useColorModeValue('white', 'gray.600');
  const focusedField = useAppSelector((s) => s.template.focusedField);
  const form = useAppSelector((s) => s.form);
  const templateColors = useAppSelector((s) => s.template.colors);
  const templateImages = useAppSelector((s) => s.template.images);
  const {
    enabled: formEnabled,
    slug,
    formFields,
    queryParams,
    buttonText,
    footerEnabled,
    footerContent,
    dataProcessingConsentText,
    dataProcessingConsentRequired,
  } = form;

  // Until the form is enabled, show the branding it will be prefilled with
  const showTemplateBranding = !formEnabled && hasDefaultBranding(form);
  const {
    backgroundColor,
    componentFillColor,
    componentFontColor,
    buttonColor,
    buttonCtaColor,
    footerColor,
  } = useMemo(
    () => (showTemplateBranding ? brandingFromTemplate(templateColors) : form),
    [showTemplateBranding, templateColors, form]
  );
  const formLogoImage =
    (showTemplateBranding && templateLogo(templateImages)) ||
    form.formLogoImage;
  const formFaviconImage =
    (showTemplateBranding && templateIcon(templateImages)) ||
    form.formFaviconImage;

  const isSlugHighlighted =
    focusedField === 'slug' || !!focusedField?.startsWith('queryParams.');
  const isFaviconHighlighted = focusedField === 'formFaviconImage';
  const isLogoHighlighted = focusedField === 'formLogoImage';
  const isButtonHighlighted =
    focusedField === 'buttonCtaColor' ||
    focusedField === 'buttonText' ||
    focusedField === 'buttonColor';
  const isFooterHighlighted =
    focusedField === 'footerContent' || focusedField === 'footerColor';
  const isFormFieldHighlighted = (fieldId: string, fieldIndex: number) =>
    (!!focusedField?.startsWith('fields.') &&
      focusedField.replace(/^fields\./, '').split('.')[0] === fieldId) ||
    !!focusedField?.startsWith(`form.formFields.${fieldIndex}.`);
  const toast = useToast();
  const isConsentHighlighted =
    focusedField === 'dataProcessingConsentText' ||
    focusedField === 'dataProcessingConsentRequired';
  const consentMeasureRef = useRef<HTMLDivElement>(null);
  const [consentIsLong, setConsentIsLong] = useState(false);
  const [consentExpanded, setConsentExpanded] = useState(false);
  const consentCollapsed = consentIsLong && !consentExpanded;
  const formConsentWidth =
    FORM_CONSENT_TEXT_WIDTH[active === 'Mobile' ? 'Mobile' : 'Desktop'][
      dataProcessingConsentRequired ? 'withCheckbox' : 'textOnly'
    ];

  useLayoutEffect(() => {
    const el = consentMeasureRef.current;
    if (!el) {
      setConsentIsLong(false);
      return undefined;
    }
    const measure = () =>
      setConsentIsLong(el.scrollHeight > FORM_CONSENT_LONG_HEIGHT);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [dataProcessingConsentText, formConsentWidth]);

  const theme = useMemo(
    () =>
      deriveTheme({
        backgroundColor,
        componentFillColor,
        componentFontColor,
        buttonColor,
        buttonCtaColor,
        footerColor,
      }),
    [
      backgroundColor,
      componentFillColor,
      componentFontColor,
      buttonColor,
      buttonCtaColor,
      footerColor,
    ]
  );

  return (
    <Box border="2px solid lightGrey" borderRadius="20px" overflow="hidden">
      <Box p={4} borderBottom="2px solid lightGrey">
        <Text textStyle="bodyFamilySemiBold" fontSize="18px" textAlign="center">
          Preview
        </Text>
      </Box>
      <Box
        p={8}
        pt={8}
        bg="white"
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap={0}
        opacity={formEnabled ? 1 : 0.5}
        pointerEvents={formEnabled ? 'auto' : 'none'}
        transition="opacity 0.2s ease-in-out"
      >
        <ButtonGroup
          isAttached
          size="sm"
          m={0}
          zIndex="1"
          sx={{
            '& > button:not(:first-of-type)': {
              borderLeft: '1px solid',
              borderColor: '#D3D3D3',
            },
          }}
        >
          {buttons.map((label) => (
            <Button
              key={label}
              onClick={() => setActive(label)}
              variant={active === label ? 'gradient' : 'grey'}
              borderRadius="12px"
            >
              {label}
            </Button>
          ))}
        </ButtonGroup>
        <Box
          w={active === 'Mobile' ? '300px' : '100%'}
          borderRadius="lg"
          boxShadow="md"
          overflow="hidden"
          bg={simulatorBg}
          transition="width 0.2s ease-in-out"
          mt={6}
        >
          <Flex
            margin={active === 'Mobile' && '0px'}
            align="center"
            justify="space-between"
            p={3}
            bg={headerBg}
          >
            <HStack
              pr={2}
              spacing={2}
              display={active === 'Mobile' ? 'none' : 'flex'}
            >
              <Circle size="12px" bg="#ff5f56" />
              <Circle size="12px" bg="#ffbd2e" />
              <Circle size="12px" bg="#27c93f" />
            </HStack>
            <Box
              flex={1}
              bg={addressBarBg}
              borderRadius="md"
              fontSize="sm"
              border={isSlugHighlighted ? HIGHLIGHT_BORDER : '1px solid'}
              borderColor={isSlugHighlighted ? HIGHLIGHT_COLOR : 'gray.300'}
              mx={active === 'Mobile' ? 0 : 1}
              maxWidth={'100%'}
              boxSizing="border-box"
            >
              <Flex align="center" justify="center" p={1}>
                <Box
                  border={isFaviconHighlighted ? HIGHLIGHT_BORDER : 'none'}
                  borderColor={HIGHLIGHT_COLOR}
                  borderRadius="4px"
                  p={isFaviconHighlighted ? 1 : 0}
                  pr={isFaviconHighlighted ? 0 : undefined}
                  margin={isFaviconHighlighted ? '-1px' : 0}
                  boxSizing="border-box"
                  display="inline-flex"
                >
                  <Image
                    h="15px"
                    src={formFaviconImage || ICON_BASE64}
                    alt="favicon-image"
                    mr={1}
                    borderRadius="4px"
                  />
                </Box>
                <Text maxWidth={'80%'} color={slug ? 'black' : 'gray.500'}>
                  {`${process.env.REACT_APP_FORMS_BASE_URL || window.env.REACT_APP_FORMS_BASE_URL}/${slug}${queryParams.length > 0 ? `?${queryParams.map((param) => `${param.id}=yourValue`).join('&')}` : ''}`}
                </Text>
                <IconButton
                  icon={<Image src={CopyIconImage} alt="copy-icon" h="16px" />}
                  size="xs"
                  aria-label="Copy URL"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `${process.env.REACT_APP_FORMS_BASE_URL || window.env.REACT_APP_FORMS_BASE_URL}/${slug}${queryParams.length > 0 ? `?${queryParams.map((param) => `${param.id}=yourValue`).join('&')}` : ''}`
                    );
                    toast({
                      render: (props) => (
                        <CustomToast
                          status="success"
                          title="URL copied to clipboard"
                          description=""
                          onClose={props.onClose}
                          position="top"
                          duration={2000}
                          id={props.id}
                        />
                      ),
                    });
                  }}
                  ml={2}
                  variant="ghost"
                  color={headerBg}
                  disabled={!slug}
                />
              </Flex>
            </Box>
          </Flex>
          <Box bg={theme.page} p={4} fontFamily="'Inter Regular', sans-serif">
            <Center height="100%">
              <Flex
                bg={theme.card}
                color={theme.text}
                w={'265px'}
                p={4}
                borderRadius={'10px'}
                border="1px solid"
                borderColor={theme.cardBorder}
                boxShadow={theme.cardShadow}
                flexDirection={'column'}
                gap={4}
                margin="48px 0 20px 0"
              >
                <Box
                  border={isLogoHighlighted ? HIGHLIGHT_BORDER : 'none'}
                  borderColor={HIGHLIGHT_COLOR}
                  borderRadius="8px"
                  p={isLogoHighlighted ? 2 : 0}
                  margin={isLogoHighlighted ? '-2px' : 0}
                  boxSizing="border-box"
                  display="inline-block"
                  alignSelf="center"
                >
                  <Image
                    src={formLogoImage}
                    alt="form-logo"
                    maxH="40px"
                    maxW="150px"
                    objectFit="contain"
                    borderRadius={'6px'}
                  />
                </Box>
                {formFields?.length > 0 && (
                  <Flex flexDirection="column" gap={3}>
                    {formFields.map((field, index) => {
                      const isHighlighted = isFormFieldHighlighted(
                        field.id,
                        index
                      );
                      return (
                        <Flex
                          key={field._clientId ?? field.id ?? `field-${index}`}
                          flexDirection={'column'}
                          gap={1.5}
                          border={isHighlighted ? HIGHLIGHT_BORDER : 'none'}
                          borderColor={HIGHLIGHT_COLOR}
                          borderRadius="8px"
                          p={isHighlighted ? 2 : 0}
                          margin={isHighlighted ? '-2px' : 0}
                          boxSizing="border-box"
                        >
                          <Flex
                            justify="space-between"
                            align="baseline"
                            gap={2}
                          >
                            <Text
                              color={theme.text}
                              fontSize={'11px'}
                              fontFamily="'Inter Medium', sans-serif"
                            >
                              {field.label}
                            </Text>
                            {!field.required && (
                              <Text color={theme.muted} fontSize={'10px'}>
                                Optional
                              </Text>
                            )}
                          </Flex>
                          <Flex
                            bg={theme.inputBg}
                            height="32px"
                            px="10px"
                            borderRadius={'8px'}
                            alignItems={'center'}
                            justifyContent={'space-between'}
                            border={'1px solid'}
                            borderColor={theme.border}
                          >
                            {field.type === 'dropdown' && (
                              <>
                                <Text
                                  fontSize={'11px'}
                                  color={theme.placeholder}
                                >
                                  {field.placeholder || 'Select an option...'}
                                </Text>
                                <ChevronDownIcon color={theme.muted} />
                              </>
                            )}
                          </Flex>
                        </Flex>
                      );
                    })}
                  </Flex>
                )}
                {dataProcessingConsentText && (
                  <Box
                    ref={consentMeasureRef}
                    aria-hidden
                    position="absolute"
                    visibility="hidden"
                    pointerEvents="none"
                    w={`${formConsentWidth}px`}
                    fontFamily="'Inter Regular', sans-serif"
                    fontSize="14px"
                    lineHeight="1.5"
                    sx={{ p: { m: 0 }, 'p + p': { mt: '10px' } }}
                  >
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw]}
                    >
                      {dataProcessingConsentText}
                    </ReactMarkdown>
                  </Box>
                )}
                {dataProcessingConsentText && (
                  <Flex
                    direction="column"
                    gap={2}
                    p={2.5}
                    bg={theme.consentBg}
                    borderRadius="8px"
                    border={isConsentHighlighted ? HIGHLIGHT_BORDER : 'none'}
                    borderColor={HIGHLIGHT_COLOR}
                  >
                    <Flex align="flex-start" gap={2}>
                      {dataProcessingConsentRequired && (
                        <Box
                          flex="none"
                          w="14px"
                          h="14px"
                          mt="2px"
                          borderRadius="4px"
                          border="1.5px solid"
                          borderColor={theme.borderHover}
                          bg={theme.inputBg}
                        />
                      )}
                      <Text
                        position="relative"
                        maxH={
                          consentCollapsed
                            ? `${PREVIEW_CONSENT_COLLAPSED}px`
                            : undefined
                        }
                        overflow={consentCollapsed ? 'hidden' : undefined}
                        color={theme.muted}
                        fontSize="11px"
                        lineHeight="1.5"
                        minW={0}
                        as="div"
                      >
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeRaw]}
                          components={{
                            a: ({ href, children }) => {
                              const url = !href
                                ? '#'
                                : href.startsWith('http://') ||
                                    href.startsWith('https://')
                                  ? href
                                  : `https://${href}`;
                              return (
                                <Link
                                  href={url}
                                  isExternal
                                  color={theme.link}
                                  fontFamily="'Inter Medium', sans-serif"
                                  textDecoration="underline"
                                  fontSize="11px"
                                >
                                  {children}
                                </Link>
                              );
                            },
                          }}
                        >
                          {dataProcessingConsentText}
                        </ReactMarkdown>
                        {consentCollapsed && (
                          <Box
                            position="absolute"
                            left={0}
                            right={0}
                            bottom={0}
                            h="36px"
                            bgGradient={`linear(to-b, transparent, ${theme.consentBg})`}
                          />
                        )}
                      </Text>
                    </Flex>
                    {consentIsLong && (
                      <Text
                        as="button"
                        type="button"
                        alignSelf="flex-start"
                        ml={dataProcessingConsentRequired ? '22px' : 0}
                        color={theme.link}
                        fontSize="11px"
                        fontFamily="'Inter SemiBold', sans-serif"
                        onClick={() => setConsentExpanded(!consentExpanded)}
                        aria-expanded={consentExpanded}
                      >
                        {consentExpanded ? 'Show less' : 'Read full text'}
                      </Text>
                    )}
                  </Flex>
                )}
                {buttonText && (
                  <Box
                    border={isButtonHighlighted ? HIGHLIGHT_BORDER : 'none'}
                    borderColor={HIGHLIGHT_COLOR}
                    boxSizing="border-box"
                    p={isButtonHighlighted ? 2 : 0}
                    margin={isButtonHighlighted ? '-2px' : 0}
                    borderRadius="8px"
                  >
                    <Center
                      bg={theme.btn}
                      color={theme.btnText}
                      boxShadow={`inset 0 0 0 1px ${theme.btnEdge}`}
                      h="34px"
                      px={4}
                      borderRadius={'8px'}
                      fontSize={'12px'}
                      fontFamily="'Inter SemiBold', sans-serif"
                      whiteSpace="nowrap"
                    >
                      {buttonText}
                    </Center>
                  </Box>
                )}
              </Flex>
            </Center>
            {footerEnabled && footerContent && (
              <Center pb={4}>
                <Box
                  border={isFooterHighlighted ? HIGHLIGHT_BORDER : 'none'}
                  borderColor={HIGHLIGHT_COLOR}
                  borderRadius="8px"
                  p={isFooterHighlighted ? 2 : 0}
                  margin={isFooterHighlighted ? '-2px' : 0}
                  boxSizing="border-box"
                >
                  <Text
                    color={theme.footer}
                    fontSize={'11px'}
                    fontFamily="'Inter Medium', sans-serif"
                  >
                    <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                      {footerContent}
                    </ReactMarkdown>
                  </Text>
                </Box>
              </Center>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CustomFormPreviewPanel;
